import {useState, useEffect, useCallback} from 'react';
import {validacion} from 'lib.common';

const {esObjeto, esFuncion, esRegExp} = validacion;

/**
 * Custom hooks para validar informacion en formularios...
 *
 * @param {object} esquemaCampos Esquema para los estados del formulario que se desean validar.
 * @param {object} esquemaValidacion Modelo de validacion para los estados.
 * @param {function} callback Funcion a ejecutar despues de realizar la validacion de todos los campos.
 */
function useValidacion(esquemaCampos = {}, esquemaValidacion = {}, callback) {
   const [campos, setCampos] = useState(esquemaCampos);
   const [existeError, setExisteError] = useState(false);
   const [isDirty, setIsDirty] = useState(false);

   // Inicialmente existeError es falso, no se realiza ninguna validacion.
   useEffect(() => {
      setExisteError(false);
   }, []);

   // Por cada cambio en los estados se validan los datos
   // para determinar si existe algun error
   useEffect(() => {
      if (isDirty) {
         setExisteError(existeErrorEstado());
      }
      // eslint-disable-next-line
   }, [campos, isDirty]);

   // Funcion que determina si existen errores en los estados
   // o los campos requeridos no tienen algun valor.
   // Envuelto en useCallback para guardar en cache la funcion y evitar
   // fugas de memoria en cada renderizacion del componente.
   const existeErrorEstado = useCallback(() => {
      const existeErrorCampo = Object.keys(esquemaValidacion).some((key) => {
         const estadoError = campos[key].error;
         return estadoError !== '';
      });

      return existeErrorCampo;
   }, [campos, esquemaValidacion]);

   // Event handler para encargarse de los cambios en los estados
   // de un componente.
   // eslint-disable-next-line
   const handleOnChange = useCallback((event) => {
      if (event.target) {
         validacionManual(event.target.name, event.target.value);
      } else {
         validacionManual(event.event.target.name, event.event.target.value);
      }
   });

   // Event handler para encargarse de los cambios los estados
   // de useValidacion.
   // eslint-disable-next-line
   const validacionManual = useCallback((name_, valor_) => {
      setIsDirty(true);
      const name = name_;
      let error = '';
      if (esquemaValidacion[name]) {
         // Si se ha enviado una funcion para castear el valor del estado
         // usamos la funcion
         const valor =
            esquemaValidacion[name].validador &&
            esquemaValidacion[name].validador.cast &&
            esFuncion(esquemaValidacion[name].validador.cast)
               ? esquemaValidacion[name].validador.cast(valor_)
               : valor_;

         // Validamos si el campo es requerido
         if (esquemaValidacion[name].requerido) {
            if (typeof valor_ === 'undefined' || valor_ === null) {
               error = 'Este campo es obligatorio.';
            } else {
               if (typeof variable !== 'boolean') {
                  if (!valor_) {
                     error = 'Este campo es obligatorio.';
                  }
               }
            }
         }
         //Aplicamos las validaciones especificadas en el esquema
         if (esObjeto(esquemaValidacion[name].validador)) {
            if (esquemaValidacion[name].validador.regla) {
               if (esFuncion(esquemaValidacion[name].validador.regla)) {
                  // Evaluar la funcion dada...
                  const errorMsg = esquemaValidacion[name].validador.regla(valor, campos);
                  if (typeof errorMsg === 'boolean' && !errorMsg) {
                     error = esquemaValidacion[name].validador.error;
                  }
                  if (typeof errorMsg === 'string' && errorMsg !== '') {
                     error = errorMsg;
                  }
               } else {
                  if (!esRegExp(esquemaValidacion[name].validador.regla)) {
                     throw new Error('La expresion regular del validador no es valida.');
                  }

                  // Evaluar la expresion regular...
                  if (valor && !esquemaValidacion[name].validador.regla.test(valor)) {
                     error = esquemaValidacion[name].validador.error;
                  }
               }
            }
         }

         setCampos((prevState) => ({
            ...prevState,
            [name]: {valor, error}
         }));
      } else {
         setCampos((prevState) => ({
            ...prevState,
            [name]: {valor: valor_, error: ''}
         }));
      }
      return error;
   });

   async function validarTodos() {
      let msgFinal = '';
      await Promise.all(
         Object.keys(esquemaValidacion).map(async (campo) => {
            const errorMsg = await validacionManual(campo, campos[campo].valor);
            msgFinal = msgFinal + errorMsg;
         })
      );
      return msgFinal === '';
   }
   // eslint-disable-next-line
   const handleOnSubmit = useCallback((event) => {
      event.preventDefault();

      // Nos aseguramos que no existan errores en el estado
      // antes de llamar la funcion de accion
      if (!existeErrorEstado() && esFuncion(callback)) {
         callback(campos);
      }
   });

   // Funcion para inicializar los estados
   const inicializar = () => {
      setCampos(esquemaCampos);
      setExisteError(false);
      setIsDirty(false);
   };

   // Funcion para asignar valores a los estados
   const setCampo = (estado, valor) => {
      const error = '';
      setCampos((prevState) => ({
         ...prevState,
         [estado]: {valor, error}
      }));
   };
   return {
      handleOnChange,
      handleOnSubmit,
      campos: campos,
      existeError,
      inicializar,
      setCampo,
      validacionManual,
      validarTodos
   };
}

export default useValidacion;
